/**
 * SupplierAgentModal.jsx
 * ======================
 * Supplier-specific chat modal. ALL supplier UI is here.
 * AgentRunModal.jsx routes here when agent_key === "supplier-create" or "supplier-creation".
 *
 * Other agents are NOT affected by this file.
 * Do NOT import this in any non-supplier context.
 */

import React from "react";
import api from "@/lib/api";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  X, Gear, Sparkle, PaperPlaneRight,
  User as UserIcon, Robot, Paperclip,
  CheckCircle, WarningCircle, Plus, CaretDown, Trash,
} from "@phosphor-icons/react";
import { useErp } from "@/context/ErpContext";
import WizardFields from "@/components/erp/WizardFields";

// ── Helpers ──────────────────────────────────────────────────────────────────

const DOW = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function ts() {
  return new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

// ── Supplier-specific sub-components ─────────────────────────────────────────

function ProgressBar({ progress }) {
  if (!progress) return null;
  const pctMatch = progress.match(/\((\d+)%\)/);
  const stepMatch = progress.match(/(\d+)\/(\d+)/);
  if (!pctMatch || !stepMatch) return null;
  const pct = parseInt(pctMatch[1], 10);
  const [, done, total] = stepMatch;
  return (
    <div className="px-4 pt-3 pb-1" data-testid="copilot-progress">
      <div className="flex justify-between text-xs text-gray-500 mb-1">
        <span>Step {done} of {total}</span>
        <span>{pct}% complete</span>
      </div>
      <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
        <div className="h-full bg-[#0020C2] transition-all duration-300" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

const SUPPLIER_QUICK_ACTIONS = [
  "Create Supplier", "Check Supplier Status", "Continue Draft",
  "Supplier Guidelines", "Contact Support",
];

function QuickActions({ onPick }) {
  return (
    <div className="flex gap-2 flex-wrap px-4 pb-2" data-testid="copilot-quick-actions">
      {SUPPLIER_QUICK_ACTIONS.map((a) => (
        <button
          key={a}
          onClick={() => onPick(a)}
          className="text-xs px-3 py-1 border border-gray-200 rounded-full text-gray-600 hover:border-[#0020C2] hover:text-[#0020C2] transition-colors"
        >
          {a}
        </button>
      ))}
    </div>
  );
}

function CapturedFieldsPanel({ fields }) {
  const entries = Object.entries(fields || {});
  if (!entries.length) return null;
  return (
    <details className="mx-4 mb-2 border border-gray-100 rounded-lg" data-testid="copilot-captured-fields">
      <summary className="cursor-pointer text-xs font-bold text-gray-500 px-3 py-2">
        Captured so far ({entries.length})
      </summary>
      <div className="px-3 pb-2 space-y-1">
        {entries.map(([field, value]) => (
          <div key={field} className="flex justify-between text-xs border-t border-gray-50 pt-1">
            <span className="text-gray-400">{field.replace(/_/g, " ")}</span>
            <span className="font-mono text-gray-700">{String(value)}</span>
          </div>
        ))}
      </div>
    </details>
  );
}

// ── Multi-select (classification, address purpose, site purpose) ──────────────

function MultiSelectChatOptions({ fieldUi, onSubmit }) {
  const [selected, setSelected] = React.useState([]);
  const options = fieldUi.options || [];
  const allowSkip = fieldUi.field === "__classification__";

  const toggle = (value) => {
    setSelected((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    );
  };

  return (
    <div className="mt-2 border border-[#0020C2]/20 rounded-lg overflow-hidden bg-white"
      data-testid={`chat-field-ui-${fieldUi.field}`}>
      <div className="max-h-48 overflow-y-auto divide-y divide-gray-50">
        {options.length === 0 && (
          <div className="px-3 py-2 text-xs text-gray-400">No options available.</div>
        )}
        {options.map((opt) => {
          const checked = selected.includes(opt.value);
          return (
            <label key={opt.value}
              className="flex items-center gap-2 px-3 py-2 text-xs hover:bg-[#0020C2]/5 cursor-pointer">
              <input type="checkbox" checked={checked} onChange={() => toggle(opt.value)}
                className="accent-[#0020C2] w-3.5 h-3.5" />
              <span className={checked ? "text-[#0020C2] font-medium" : "text-gray-700"}>{opt.label}</span>
            </label>
          );
        })}
      </div>
      <div className="flex">
        <button onClick={() => selected.length && onSubmit(selected)}
          disabled={selected.length === 0}
          className="flex-1 text-xs font-bold px-3 py-2 bg-[#0020C2] text-white disabled:bg-gray-200 disabled:text-gray-400 transition-colors">
          {selected.length ? `Select ${selected.length} item${selected.length > 1 ? "s" : ""}` : "Select above"}
        </button>
        {allowSkip && (
          <button onClick={() => onSubmit(["none"])}
            className="text-xs font-bold px-3 py-2 bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors border-l border-gray-200">
            None
          </button>
        )}
      </div>
    </div>
  );
}

// ── Searchable dropdown (procurement BU, country, etc.) ───────────────────────

function SearchableChatOptions({ fieldUi, onPick }) {
  const [q, setQ] = React.useState("");
  const options = fieldUi.options || [];
  const filtered = q ? options.filter((o) => o.label.toLowerCase().includes(q.toLowerCase())) : options;
  return (
    <div className="mt-2 border border-[#0020C2]/20 rounded-lg overflow-hidden bg-white"
      data-testid={`chat-field-ui-${fieldUi.field}`}>
      <input autoFocus value={q} onChange={(e) => setQ(e.target.value)}
        placeholder={`Search ${fieldUi.field.replace(/_/g, " ")}…`}
        className="w-full px-3 py-2 text-xs outline-none border-b border-gray-100" />
      <div className="max-h-40 overflow-y-auto">
        {options.length === 0 && <div className="px-3 py-2 text-xs text-gray-400">No options available right now.</div>}
        {options.length > 0 && filtered.length === 0 && <div className="px-3 py-2 text-xs text-gray-400">No matches</div>}
        {filtered.slice(0, 50).map((opt) => (
          <div key={opt.value} onClick={() => onPick(opt)}
            className="px-3 py-1.5 text-xs hover:bg-[#0020C2]/5 cursor-pointer border-b border-gray-50 last:border-0">
            {opt.label}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Radio / Yes-No buttons ────────────────────────────────────────────────────

function RadioOptions({ fieldUi, onPick }) {
  const options = fieldUi.options || [];
  return (
    <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`chat-field-ui-${fieldUi.field}`}>
      {options.map((opt) => (
        <button key={opt.value} onClick={() => onPick(opt)}
          className="text-xs px-3 py-1.5 border border-[#0020C2]/30 rounded-full text-[#0020C2] bg-[#0020C2]/5 hover:bg-[#0020C2]/10 transition-colors">
          {opt.label}
        </button>
      ))}
    </div>
  );
}

// ── Attachment gate ───────────────────────────────────────────────────────────

function AttachmentGate({ fieldUi, onOptionClick }) {
  return (
    <div className="mt-2 rounded-lg border border-[#0020C2]/20 overflow-hidden bg-white"
      data-testid="chat-attachment-gate">
      <div className="px-3 py-2 text-xs text-gray-500 bg-gray-50 border-b border-gray-100">
        {fieldUi.uploadedCount > 0
          ? `${fieldUi.uploadedCount} file${fieldUi.uploadedCount > 1 ? "s" : ""} uploaded`
          : "No files uploaded yet — use the 📎 attach button"}
      </div>
      {fieldUi.uploadedCount > 0 && fieldUi.options?.length > 0 && (
        <button onClick={() => onOptionClick?.(fieldUi.options[0])}
          data-testid="attachment-done-btn"
          className="w-full text-xs font-bold px-3 py-2 bg-[#0020C2] text-white hover:bg-[#001AA3] transition-colors">
          Done — proceed with {fieldUi.uploadedCount} file{fieldUi.uploadedCount > 1 ? "s" : ""}
        </button>
      )}
    </div>
  );
}

// ── Chat Markdown ─────────────────────────────────────────────────────────────

function ChatMarkdown({ children }) {
  return (
    <div className="chat-md text-sm leading-relaxed">
      <ReactMarkdown remarkPlugins={[remarkGfm]}
        components={{
          p: ({ node, ...props }) => <p className="mb-2 last:mb-0 whitespace-pre-wrap" {...props} />,
          strong: ({ node, ...props }) => <strong className="font-bold text-gray-900" {...props} />,
          ul: ({ node, ...props }) => <ul className="list-disc pl-5 mb-2 space-y-0.5" {...props} />,
          ol: ({ node, ...props }) => <ol className="list-decimal pl-5 mb-2 space-y-0.5" {...props} />,
          li: ({ node, ...props }) => <li className="text-sm" {...props} />,
          h3: ({ node, ...props }) => <h3 className="text-sm font-bold mb-1 mt-2 text-[#0020C2]" {...props} />,
          code: ({ node, inline, ...props }) =>
            inline
              ? <code className="font-mono text-[12px] bg-gray-100 px-1 py-0.5 rounded" {...props} />
              : <code className="font-mono text-[12px] block bg-gray-50 border border-gray-200 rounded p-2 overflow-x-auto" {...props} />,
        }}>
        {children}
      </ReactMarkdown>
    </div>
  );
}

// ── Supplier Chat Bubble ──────────────────────────────────────────────────────

function SupplierChatBubble({ msg, onOptionClick, onMultiSubmit }) {
  const isUser = msg.role === "user";
  const fieldUi = !isUser && !msg.loading ? msg.result?.next_field_ui : null;

  return (
    <div className={`flex gap-2.5 ${isUser ? "flex-row-reverse" : "flex-row"}`}>
      <div className={`shrink-0 w-6 h-6 flex items-center justify-center rounded-full text-white
        ${isUser ? "bg-[#0020C2]" : "bg-gray-700"}`}>
        {isUser ? <UserIcon size={11} weight="bold" /> : <Robot size={11} weight="bold" />}
      </div>

      <div className={`max-w-[82%] rounded-2xl px-3.5 py-2 text-sm leading-relaxed
        ${isUser
          ? "bg-[#0020C2] text-white rounded-tr-sm"
          : "bg-white border border-gray-200 text-gray-900 rounded-tl-sm shadow-sm"}`}>

        {msg.loading && (
          <div className="flex items-center gap-1 py-0.5">
            {[0, 150, 300].map((d) => (
              <span key={d} className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce"
                style={{ animationDelay: `${d}ms` }} />
            ))}
          </div>
        )}

        {!msg.loading && msg.text && (
          isUser
            ? <p className="whitespace-pre-wrap">{msg.text}</p>
            : <ChatMarkdown>{msg.text}</ChatMarkdown>
        )}

        {/* Supplier field UI widgets */}
        {fieldUi?.uiType === "multi_select" && (
          <MultiSelectChatOptions fieldUi={fieldUi} onSubmit={(values) => onMultiSubmit?.(values)} />
        )}
        {fieldUi?.uiType === "searchable_dropdown" && (
          <SearchableChatOptions fieldUi={fieldUi} onPick={(opt) => onOptionClick?.(opt)} />
        )}
        {fieldUi?.uiType === "attachment_gate" && (
          <AttachmentGate fieldUi={fieldUi} onOptionClick={onOptionClick} />
        )}
        {fieldUi?.uiType === "radio" && (
          <RadioOptions fieldUi={fieldUi} onPick={(opt) => onOptionClick?.(opt)} />
        )}
        {/* Fallback: plain buttons for any other uiType with options */}
        {fieldUi &&
          !["searchable_dropdown", "multi_select", "attachment_gate", "radio"].includes(fieldUi.uiType) &&
          fieldUi.options?.length > 0 && (
            <div
              className={fieldUi.uiType === "dropdown" ? "mt-2 flex flex-col gap-1" : "mt-2 flex flex-wrap gap-1.5"}
              data-testid={`chat-field-ui-${fieldUi.field}`}>
              {fieldUi.options.map((opt) => (
                <button key={opt.value} onClick={() => onOptionClick?.(opt)}
                  className="text-xs px-3 py-1.5 border border-[#0020C2]/30 rounded-full text-[#0020C2] bg-[#0020C2]/5 hover:bg-[#0020C2]/10 transition-colors text-left">
                  {opt.label}
                </button>
              ))}
            </div>
          )}

        {msg.timestamp && (
          <div className={`text-[9px] mt-1 opacity-40 ${isUser ? "text-right" : "text-left"}`}>
            {msg.timestamp}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Input Bar ─────────────────────────────────────────────────────────────────

function SupplierInputBar({ inputRef, value, onChange, onKeyDown, onSend, busy,
  onAttach, attaching, attachedCount }) {
  const fileInputRef = React.useRef(null);
  return (
    <div className="border-t border-gray-200 px-4 py-3 bg-white shrink-0">
      <div className="flex items-end gap-2 border border-gray-300 bg-gray-50
        focus-within:border-[#0020C2] focus-within:ring-2 focus-within:ring-blue-100">
        {onAttach && (
          <>
            <input ref={fileInputRef} type="file"
              accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
              multiple className="hidden"
              onChange={(e) => {
                const files = Array.from(e.target.files || []);
                if (files.length) onAttach(files);
                e.target.value = "";
              }}
              data-testid="agent-attach-input" />
            <button onClick={() => fileInputRef.current?.click()}
              disabled={attaching}
              title="Attach a file"
              data-testid="agent-attach-button"
              className="m-2 w-8 h-8 flex items-center justify-center text-gray-500 hover:text-[#0020C2]
                hover:bg-gray-100 rounded transition-colors shrink-0 disabled:opacity-40 relative">
              {attaching
                ? <span className="w-3 h-3 border-2 border-gray-300 border-t-[#0020C2] rounded-full animate-spin" />
                : <Paperclip size={16} weight="bold" />}
              {attachedCount > 0 && (
                <span className="absolute -top-1 -right-1 w-4 h-4 bg-[#0020C2] text-white text-[9px] rounded-full flex items-center justify-center">
                  {attachedCount}
                </span>
              )}
            </button>
          </>
        )}
        <textarea ref={inputRef} value={value} onChange={onChange} onKeyDown={onKeyDown}
          rows={1} disabled={busy}
          placeholder="Continue the conversation…"
          data-testid="agent-prompt"
          className="flex-1 bg-transparent px-3 py-2.5 text-sm resize-none outline-none
            placeholder-gray-400 disabled:opacity-50 leading-relaxed"
          style={{ minHeight: "2.5rem", maxHeight: "7.5rem" }} />
        <button onClick={onSend} disabled={busy || !value.trim()}
          className="m-2 w-8 h-8 bg-[#0020C2] text-white flex items-center justify-center
            hover:bg-[#001AA3] disabled:opacity-40 transition-colors shrink-0">
          {busy
            ? <span className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            : <PaperPlaneRight size={14} weight="fill" />}
        </button>
      </div>
      <span className="text-[10px] text-gray-400 mt-1 block">↵ send · shift+↵ new line</span>
    </div>
  );
}

// ── Chat History Control ──────────────────────────────────────────────────────

function ChatHistoryControl({ agentKey, currentSessionId, onNewChat, onOpenSession }) {
  const [open, setOpen] = React.useState(false);
  const [sessions, setSessions] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const wrapRef = React.useRef(null);

  const load = React.useCallback(async () => {
    if (!agentKey) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/erp-agents/${agentKey}/sessions`);
      setSessions(Array.isArray(data) ? data : []);
    } catch {
      setSessions([]);
    } finally {
      setLoading(false);
    }
  }, [agentKey]);

  React.useEffect(() => { if (open) load(); }, [open, load]);

  React.useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  const deleteSession = async (sid, e) => {
    e.stopPropagation();
    if (!window.confirm("Delete this conversation?")) return;
    try {
      await api.delete(`/erp-agents/${agentKey}/sessions/${sid}`);
      load();
    } catch { /* ignore */ }
  };

  return (
    <div className="flex items-center gap-3" ref={wrapRef}>
      <button onClick={onNewChat}
        className="text-[11px] font-semibold text-gray-500 hover:text-[#0020C2] flex items-center gap-1 shrink-0"
        data-testid="chat-new" title="Start a new conversation">
        <Plus size={13} weight="bold" /> New Chat
      </button>
      <div className="relative">
        <button onClick={() => setOpen((v) => !v)}
          className="text-[11px] font-semibold text-gray-500 hover:text-[#0020C2] flex items-center gap-1"
          data-testid="chat-history-toggle">
          History
          <CaretDown size={10} weight="bold" className={`transition-transform ${open ? "rotate-180" : ""}`} />
        </button>
        {open && (
          <div className="absolute right-0 z-40 mt-2 w-72 bg-white border border-gray-200 rounded-lg shadow-lg max-h-80 overflow-y-auto"
            data-testid="chat-history-list">
            {loading && <div className="px-3 py-2 text-xs text-gray-400">Loading…</div>}
            {!loading && sessions.length === 0 && (
              <div className="px-3 py-2 text-xs text-gray-400">No previous conversations.</div>
            )}
            {sessions.map((s) => (
              <div key={s.session_id}
                onClick={() => { onOpenSession(s.session_id); setOpen(false); }}
                className={`px-3 py-2 text-xs cursor-pointer border-b border-gray-50 last:border-0
                  hover:bg-[#0020C2]/5 flex items-center gap-2
                  ${s.session_id === currentSessionId ? "bg-[#0020C2]/5" : ""}`}>
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-gray-800 truncate">{s.title}</div>
                  <div className="text-[10px] text-gray-400">
                    {s.updated_at ? new Date(s.updated_at).toLocaleString([], {
                      month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
                    }) : ""}
                  </div>
                </div>
                <button onClick={(e) => deleteSession(s.session_id, e)}
                  className="text-[#DC2626] shrink-0 p-1">
                  <Trash size={11} weight="bold" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main Supplier Modal ───────────────────────────────────────────────────────

export default function SupplierAgentModal({ onClose }) {
  const { openAgent, setOpenAgent, setSetupOpen, profile, agents } = useErp();

  const agent = agents.find((a) => a.key === openAgent);

  // Session persistence
  const sessionKey = "supplier_session_id";
  const [sessionId, setSessionId] = React.useState(() => {
    const stored = sessionStorage.getItem(sessionKey);
    if (stored) return stored;
    const fresh = crypto.randomUUID();
    sessionStorage.setItem(sessionKey, fresh);
    return fresh;
  });

  const [messages, setMessages] = React.useState([]);
  const [input, setInput] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [chatStarted, setChatStarted] = React.useState(false);
  const [copilotProgress, setCopilotProgress] = React.useState(null);
  const [copilotFields, setCopilotFields] = React.useState(null);
  const [attaching, setAttaching] = React.useState(false);
  const [attachedCount, setAttachedCount] = React.useState(0);
  const [fieldValues, setFieldValues] = React.useState({});
  const [missingFields, setMissingFields] = React.useState([]);

  const messagesEndRef = React.useRef(null);
  const inputRef = React.useRef(null);

  React.useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // ── File attachment ─────────────────────────────────────────────────────────

  const handleAttachFiles = async (files) => {
    setAttaching(true);
    const results = [];
    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append("file", file);
        const { data } = await api.post(
          `/erp-agents/supplier-creation/${sessionId}/upload-attachment`,
          formData,
          { headers: { "Content-Type": "multipart/form-data" } }
        );
        setAttachedCount(data.attachments_count);
        results.push({ name: file.name, ok: true });
      } catch (e) {
        const msg = e?.response?.data?.detail || `Failed to upload ${file.name}`;
        results.push({ name: file.name, ok: false, error: msg });
      }
    }
    setAttaching(false);
    const succeeded = results.filter(r => r.ok).map(r => r.name);
    const failed = results.filter(r => !r.ok);
    let summary = "";
    if (succeeded.length) summary += `📎 Attached: ${succeeded.join(", ")}`;
    if (failed.length) summary += (summary ? "\n" : "") + failed.map(f => `❌ ${f.name}: ${f.error}`).join("\n");
    setMessages((prev) => [...prev, {
      id: Date.now(), role: "assistant", timestamp: ts(), text: summary || "Upload complete.",
    }]);
    if (succeeded.length > 0) {
      setTimeout(() => sendMessage("__refresh_attachment_ui__"), 300);
    }
  };

  // ── Send message ────────────────────────────────────────────────────────────

  const sendMessage = async (text) => {
    const userText = (text || "").trim();
    if (!userText || busy) return;
    setInput("");
    if (inputRef.current) inputRef.current.style.height = "auto";
    if (!chatStarted) setChatStarted(true);

    const now = ts();
    const isInternalRefresh = userText === "__refresh_attachment_ui__";

    if (!isInternalRefresh) {
      setMessages((prev) => [
        ...prev,
        { id: Date.now(), role: "user", text: userText, timestamp: now },
        { id: Date.now() + 1, role: "assistant", loading: true },
      ]);
    } else {
      setMessages((prev) => [...prev, { id: Date.now() + 1, role: "assistant", loading: true }]);
    }
    setBusy(true);

    try {
      const res = await api.post("/erp-agents/supplier-creation/chat", {
        session_id: sessionId,
        message:    userText,
      });
      const data = res.data;
      if (data?.progress) setCopilotProgress(data.progress);
      if (data?.fields_collected) {
        setCopilotFields(data.fields_collected);
        // Sync captured fields to wizard — must match original AgentRunModal mapping exactly
        const CHAT_TO_WIZARD = {
          supplier_city:    "city",
          supplier_country: "country",
          supplier_state:   "state",
          supplier_site:    "supplier_site",  // restored — was missing
          // Bank account fields — same pattern as supplier fields above
          bank_name:                    "bank_name",
          bank_branch_name:             "bank_branch_name",
          bank_account_number:          "bank_account_number",
          iban:                         "bank_account_number",
          bank_account_name:            "bank_account_name",
          bank_currency:                "bank_currency",
          account_type:                 "account_type",
          allow_international_payments: "allow_international_payments",
          bank_primary_flag:            "bank_primary_flag",
        };
        setFieldValues(prev => {
          const merged = { ...prev };
          for (const [chatKey, val] of Object.entries(data.fields_collected)) {
            if (val === null || val === undefined || val === "") continue;
            const wizardKey = CHAT_TO_WIZARD[chatKey] || chatKey;
            // procurement_bus is a list — wizard needs first element as string
            const wizardVal = (chatKey === "procurement_bus")
              ? (Array.isArray(val) ? val[0] : val)
              : val;
            if (wizardVal && !merged[wizardKey]) {
              merged[wizardKey] = wizardVal;
            }
          }
          // procurement_bus list → procurement_bu wizard field
          const buList = data.fields_collected.procurement_bus;
          if (buList && !prev.procurement_bu) {
            merged.procurement_bu = Array.isArray(buList) ? buList[0] : buList;
          }
          // procurement_bu scalar (set during collecting_extra_bu flow)
          const buScalar = data.fields_collected.procurement_bu;
          if (buScalar && typeof buScalar === "string" && !prev.procurement_bu) {
            merged.procurement_bu = buScalar;
          }
          return merged;
        });
      }

      const reply = data?.reply || data?.response || data?.message || "Done.";
      const now2 = ts();
      setMessages((prev) =>
        prev.map((m) =>
          m.loading
            ? { ...m, loading: false, text: reply, result: data, timestamp: now2 }
            : m
        )
      );
    } catch (e) {
      setMessages((prev) =>
        prev.map((m) =>
          m.loading
            ? { ...m, loading: false, timestamp: ts(), text: `Error: ${e?.response?.data?.detail || e.message}` }
            : m
        )
      );
    } finally {
      setBusy(false);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  };

  const handleInputChange = (e) => {
    setInput(e.target.value);
    e.target.style.height = "auto";
    e.target.style.height = Math.min(e.target.scrollHeight, 120) + "px";
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(input); }
  };

  // ── New chat / open session ─────────────────────────────────────────────────

  const startNewChat = async () => {
    let newId = crypto.randomUUID();
    try {
      const { data } = await api.post(`/erp-agents/supplier-creation/sessions/new`);
      if (data?.session_id) newId = data.session_id;
    } catch { /* use local uuid */ }
    sessionStorage.setItem(sessionKey, newId);
    setSessionId(newId);
    setMessages([]);
    setInput("");
    setChatStarted(false);
    setCopilotProgress(null);
    setCopilotFields(null);
    setAttachedCount(0);
    setFieldValues({});
  };

  const openSession = async (sid) => {
    try {
      const { data } = await api.get(`/erp-agents/supplier-creation/sessions/${sid}/messages`);
      const loaded = (data?.messages || []).map((m, i) => ({
        id: Date.now() + i, role: m.role, text: m.content,
        timestamp: m.created_at
          ? new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
          : ts(),
        result: m.next_field_ui ? { next_field_ui: m.next_field_ui } : undefined,
      }));
      sessionStorage.setItem(sessionKey, sid);
      setSessionId(sid);
      setMessages(loaded);
      setChatStarted(true);
    } catch (e) {
      window.alert(`Could not open conversation: ${e?.response?.data?.detail || e.message}`);
    }
  };

  const close = () => {
    setOpenAgent(null);
    onClose?.();
  };

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <>
      <style>{`
        @keyframes chatFadeIn    { from { opacity:0; transform:translateX(-10px); } to { opacity:1; transform:translateX(0); } }
        @keyframes wizardSlideIn { from { opacity:0; transform:translateX(20px);  } to { opacity:1; transform:translateX(0); } }
        .supplier-modal-pre  { width:672px; max-width:calc(100vw - 2rem); max-height:90vh; }
        .supplier-modal-chat { width:min(92vw,1400px); height:90vh; }
      `}</style>

      <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={(e)=>{if(!chatStarted) close}}>
        <div onClick={(e) => e.stopPropagation()}
          data-testid={`agent-modal-${openAgent}`}
          className={`bg-white text-gray-900 border border-gray-300 flex flex-col overflow-hidden
            ${chatStarted ? "supplier-modal-chat" : "supplier-modal-pre"}`}
          style={{ transition: "width 0.45s cubic-bezier(0.4,0,0.2,1), height 0.45s cubic-bezier(0.4,0,0.2,1)" }}>

          {/* Header */}
          <div className="px-5 py-4 border-b border-gray-200 flex items-start justify-between shrink-0">
            <div>
              <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-gray-500">
                Oracle Fusion ERP · Vaku AI
              </div>
              <h3 className="text-2xl font-black tracking-tight mt-0.5">{agent?.label}</h3>
              <p className="text-xs text-gray-500 mt-0.5">{agent?.hint}</p>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              {chatStarted && (
                <>
                  <ChatHistoryControl
                    agentKey={openAgent}
                    currentSessionId={sessionId}
                    onNewChat={startNewChat}
                    onOpenSession={openSession}
                  />
                  <div className="w-px h-5 bg-gray-200" />
                </>
              )}
              <button onClick={() => { close(); setSetupOpen(true); }}
                className="text-[11px] font-semibold text-gray-500 hover:text-[#0020C2] flex items-center gap-1">
                <Gear size={13} weight="bold" /> {profile?.oracle_username || "Set up profile"}
              </button>
              <button onClick={close} data-testid="agent-modal-close">
                <X size={18} />
              </button>
            </div>
          </div>

          {/* Body */}
          {!chatStarted ? (

            /* Pre-chat — wizard + input */
            <>
              <div className="flex-1 overflow-y-auto p-5 space-y-4">
                <WizardFields
                  agentKey={openAgent}
                  values={fieldValues}
                  onChange={setFieldValues}
                  missingFields={missingFields}
                  onCreateNew={() => {}}
                  fieldErrors={{}}
                  fieldWarnings={{}}
                />
              </div>
              <SupplierInputBar
                inputRef={inputRef}
                value={input}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                onSend={() => sendMessage(input)}
                busy={busy}
              />
            </>

          ) : (

            /* Chat mode — 60% chat / 40% wizard */
            <div className="flex flex-1 overflow-hidden">

              {/* LEFT — chat */}
              <div className="flex flex-col overflow-hidden border-r border-gray-200"
                style={{ width: "60%", animation: "chatFadeIn 0.4s cubic-bezier(0.4,0,0.2,1)" }}>

                <ProgressBar progress={copilotProgress} />
                <CapturedFieldsPanel fields={copilotFields} />
                {messages.length <= 1 && (
                  <QuickActions onPick={(text) => sendMessage(text)} />
                )}

                <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-gray-50">
                  {messages.map((msg) => (
                    <SupplierChatBubble
                      key={msg.id}
                      msg={msg}
                      onOptionClick={(opt) => sendMessage(opt.value)}
                      onMultiSubmit={(values) => sendMessage(values.join(", "))}
                    />
                  ))}
                  <div ref={messagesEndRef} />
                </div>

                <SupplierInputBar
                  inputRef={inputRef}
                  value={input}
                  onChange={handleInputChange}
                  onKeyDown={handleKeyDown}
                  onSend={() => sendMessage(input)}
                  busy={busy}
                  onAttach={handleAttachFiles}
                  attaching={attaching}
                  attachedCount={attachedCount}
                />
              </div>

              {/* RIGHT — wizard */}
              <div className="flex flex-col bg-white"
                style={{ width: "40%", minWidth: "360px", animation: "wizardSlideIn 0.45s cubic-bezier(0.4,0,0.2,1)" }}>
                <div className="flex-1 overflow-y-auto p-4 space-y-4">
                  <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#0020C2] flex items-center gap-1.5">
                    <Sparkle size={11} weight="fill" /> Wizard fields
                    <span className="text-gray-400 normal-case font-normal tracking-normal ml-1">
                      · auto-filled as you chat
                    </span>
                  </div>
                  <WizardFields
                    agentKey={openAgent}
                    values={fieldValues}
                    onChange={setFieldValues}
                    missingFields={missingFields}
                    onCreateNew={() => {}}
                    fieldErrors={{}}
                    fieldWarnings={{}}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}